import { OrderTrackingShipmentStatus } from "@redotech/order-tracking-common/order-tracking-types";
import type {
  TrackingPageOrder,
  TrackingPageShipment,
} from "@redotech/order-tracking-common/tracking-page-order-definition";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import {
  getCarrierTrackingUrl,
  resolveCarrier,
} from "@redotech/redo-order-tracking-settings-common/carriers/carrier-resolver";
import type { TrackingTextRule } from "@redotech/redo-order-tracking-settings-common/tracking-text-rule";
import { memo, useMemo } from "react";
import { Flex } from "../../../flex";
import { ExternalLink } from "../../../link";
import { PickupConfig } from "../../../settings-elements/order/pick-up-config-util";
import { Text } from "../../../text";
import { TrackingPageContext } from "../../context/tracking-page-context";
import {
  getBodyStyles,
  getDeliveryDateStyles,
  getHeaderStyles,
  getLabelStyles,
  getLinkStyles,
  getTrackingNumberUrlStyles,
} from "../shared/block-styles";
import { shouldHidePosTrackingDetails } from "./tracking-details-pos-utils";
import {
  CarrierBrandIcon,
  formatDeliveryDate,
  getShipmentStatusText,
  shouldHideDeliveryEstimateSection,
  type StatusLabelOverrides,
} from "./tracking-details-shared";
import { CollapsibleTimeline } from "./tracking-details-timeline";
import type { TrackingMapStyle } from "./tracking-map";
import { TrackingMap } from "./tracking-map";

export const TrackingDetailsMobile = memo(function TrackingDetailsMobile({
  shipment,
  order,
  pickupConfig,
  stalledDays,
  optionalSubtext,
  title,
  showMap,
  mapStyle,
  statusLabelOverrides,
  hideDeliveryEstimateWhenUnknown,
  textRules,
  hideTrackingNumber,
}: {
  shipment: TrackingPageShipment | undefined;
  order: TrackingPageOrder;
  pickupConfig: PickupConfig;
  stalledDays: number | null;
  optionalSubtext: string | undefined;
  title: string | undefined;
  showMap: boolean | undefined;
  mapStyle: TrackingMapStyle;
  statusLabelOverrides: StatusLabelOverrides | undefined;
  hideDeliveryEstimateWhenUnknown: boolean | undefined;
  textRules?: readonly TrackingTextRule[];
  hideTrackingNumber?: boolean;
}) {
  const { returnAppSettings, pageSettings } =
    useRequiredContext(TrackingPageContext);
  const { t } = useTranslation();

  const headerStyles = getHeaderStyles(pageSettings);
  const bodyStyles = getBodyStyles(pageSettings);
  const labelStyles = getLabelStyles(pageSettings);
  const deliveryDateStyles = getDeliveryDateStyles(pageSettings);
  const trackingUrlStyles = getTrackingNumberUrlStyles(pageSettings);
  const linkStyles = getLinkStyles(pageSettings);

  const isDelivered =
    shipment?.status === OrderTrackingShipmentStatus.DELIVERED;
  const deliveryDate = isDelivered
    ? shipment?.deliveredAt
    : shipment?.estimatedDeliveryAt;
  const eddDistrusted = useMemo(() => {
    const resolved = shipment?.carrier
      ? resolveCarrier(shipment.carrier)
      : undefined;
    return !!(resolved?.found && resolved.definition.distrustDeliveryEstimate);
  }, [shipment?.carrier]);

  const hideEstimateSection = shouldHideDeliveryEstimateSection({
    hideWhenUnknown: hideDeliveryEstimateWhenUnknown,
    date: deliveryDate,
    isDelivered,
  });

  const trackingEvents = useMemo(
    () => (shipment?.trackingEvents?.length ? shipment.trackingEvents : []),
    [shipment?.trackingEvents],
  );
  const orderedEvents = useMemo(
    () =>
      [...trackingEvents].sort(
        (a, b) =>
          (b.timestamp?.getTime?.() ?? 0) - (a.timestamp?.getTime?.() ?? 0),
      ),
    [trackingEvents],
  );

  const trackingCode = shipment?.trackingCode;
  const trackingUrl =
    getCarrierTrackingUrl(
      shipment?.carrier ?? undefined,
      trackingCode ?? undefined,
    ) ?? shipment?.trackingUrl;
  const showTrackingLink =
    !!trackingUrl &&
    !order.cancelledAt &&
    returnAppSettings.storeUrl !== "shopconzuri.myshopify.com";

  if (shouldHidePosTrackingDetails(order, shipment)) {
    return null;
  }

  return (
    <Flex dir="column" gap="xl" p="3xl" w="full">
      {!hideEstimateSection && (
        <EstimatedDeliverySection
          bodyStyles={bodyStyles}
          date={deliveryDate ?? null}
          deliveryDateStyles={deliveryDateStyles}
          eddDistrusted={eddDistrusted}
          headerStyles={headerStyles}
          isDelivered={isDelivered}
          labelStyles={labelStyles}
          title={title}
        />
      )}

      {showMap && (
        <TrackingMap
          mapStyle={mapStyle}
          order={order}
          shipment={shipment}
          statusLabelOverrides={statusLabelOverrides}
        />
      )}

      <Flex dir="column" gap="xxs">
        <Text
          fontSize="xs"
          fontWeight="medium"
          style={labelStyles}
          textColor="secondary"
        >
          {t("Status")}
        </Text>
        <Text fontSize="md" fontWeight="semibold" style={bodyStyles}>
          {getShipmentStatusText({
            cancelReason: order.cancelReason,
            pickupConfig,
            shipment,
            source: order.sourceName,
            stalledDays,
            statusLabelOverrides,
          })}
        </Text>
      </Flex>

      {orderedEvents.length > 0 && (
        <CollapsibleTimeline
          bodyStyles={bodyStyles}
          events={orderedEvents}
          labelStyles={labelStyles}
          linkStyles={linkStyles}
          textRules={textRules}
        />
      )}

      {!hideTrackingNumber && trackingCode && (
        <Flex dir="column" gap="xxs">
          <Text
            fontSize="xs"
            fontWeight="medium"
            style={labelStyles}
            textColor="secondary"
          >
            {t("Tracking")}
          </Text>
          <Flex align="center" gap="xs">
            <CarrierBrandIcon carrier={shipment?.carrier ?? null} />
            {showTrackingLink && trackingUrl ? (
              <ExternalLink
                showIcon={false}
                style={linkStyles}
                url={trackingUrl}
              >
                <Text
                  fontWeight="semibold"
                  style={{
                    ...trackingUrlStyles,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  textColor="secondary"
                >
                  {trackingCode}
                </Text>
              </ExternalLink>
            ) : (
              <Text
                fontWeight="semibold"
                style={{
                  ...bodyStyles,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                textColor="secondary"
              >
                {trackingCode}
              </Text>
            )}
          </Flex>
        </Flex>
      )}

      {optionalSubtext && (
        <Text fontSize="sm" style={labelStyles}>
          {optionalSubtext}
        </Text>
      )}
    </Flex>
  );
});

const EstimatedDeliverySection = memo(function EstimatedDeliverySection({
  date,
  isDelivered,
  eddDistrusted,
  headerStyles,
  bodyStyles,
  title: titleOverride,
}: {
  date: Date | null;
  isDelivered: boolean;
  eddDistrusted: boolean;
  headerStyles: React.CSSProperties;
  bodyStyles: React.CSSProperties;
  labelStyles: React.CSSProperties;
  deliveryDateStyles: React.CSSProperties;
  title: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const title =
    titleOverride ?? (isDelivered ? t("Delivered") : t("Estimated delivery"));

  return (
    <Flex dir="column" gap="xs">
      <Text fontSize="xl" fontWeight="semibold" style={headerStyles}>
        {title}
      </Text>
      {date ? (
        <Text fontSize="md" fontWeight="semibold" style={bodyStyles}>
          {formatDeliveryDate(date, i18n.language)}
        </Text>
      ) : (
        <Text fontSize="md" style={bodyStyles}>
          {eddDistrusted ? t("Unknown ETA") : t("Awaiting ETA")}
        </Text>
      )}
    </Flex>
  );
});
